import type { AssemblyTransformHistoryRecord } from '../component-instance';

/**
 * 装配体 transform 历史提交结果。
 */
export type AssemblyTransformHistoryCommitResult = void | Promise<void>;

/**
 * 移动零部件输入模式。
 * 与 SolidWorks“移动零部件”下拉列表选项保持一致。
 */
export enum MoveComponentInputMode {
  /** 自由拖动，允许沿任意方向拖动。 */
  FreeDrag = 'free-drag',
  /** 沿装配体 XYZ 轴方向拖动。 */
  AlongAssemblyAxis = 'along-assembly-axis',
  /** 沿选定实体拖动，例如直线、边、轴或平面。 */
  AlongEntity = 'along-entity',
  /** 输入 Delta XYZ 增量后应用。 */
  DeltaXYZ = 'delta-xyz',
  /** 移动到指定 XYZ 绝对坐标。 */
  ToXYZPosition = 'to-xyz-position',
}

/**
 * 移动零部件选项模式。
 */
export enum MoveComponentOptionMode {
  /** 标准拖动，不启用额外检测。 */
  Standard = 'standard',
  /** 碰撞检查，检测与其他零部件的接触。 */
  CollisionDetection = 'collision-detection',
}

//------ 移动旋转零部件碰撞检查选项 开始------
/**
 * 移动/旋转零部件碰撞检查范围。
 */
export enum MoveComponentCollisionCheckScope {
  /** 检查拖动零部件与装配体中所有其它零部件之间的碰撞。 */
  AllComponents = 'all-components',
  /** 仅检查拖动零部件与用户指定零部件之间的碰撞。 */
  SelectedComponents = 'selected-components',
}

/**
 * 碰撞检查零部件选择阶段。
 */
export enum MoveComponentCollisionSelectionPhase {
  /** 正常拖动阶段，视口点击会进入移动或旋转拖拽。 */
  Dragging = 'dragging',
  /** 正在选择“供碰撞检查的零部件”，视口点击只追加到选择框。 */
  SelectingComponents = 'selecting-components',
}

/**
 * 碰撞检查运行配置。
 */
export interface MoveComponentCollisionDetectionConfig {
  /** 当前是否启用碰撞检查。 */
  enabled: boolean;
  /** 当前碰撞检查范围。 */
  scope: MoveComponentCollisionCheckScope;
  /** “这些零部件之间”模式下参与碰撞检查的零部件对象 ID。 */
  componentObjectIds: readonly string[];
  /** 是否在发生碰撞时停止拖动或旋转。 */
  stopAtCollision: boolean;
  /** 是否只检查当前被拖动的主零部件，不包含配合或阵列联动零部件。 */
  draggedPartOnly: boolean;
}
//------ 移动旋转零部件碰撞检查选项 结束------

/**
 * 三轴数值输入元组。
 */
export type MoveComponentXYZTuple = readonly [number, number, number];

/**
 * 沿实体移动的几何约束类型。
 */
export enum MoveAlongEntityConstraintKind {
  /** 沿直线方向移动，具有一个自由度。 */
  LineLike = 'line-like',
  /** 在平面内移动，具有两个自由度。 */
  PlaneLike = 'plane-like',
}

/**
 * 沿实体移动参考对象类型。
 */
export enum MoveAlongEntityReferenceType {
  /** 基准轴。 */
  DatumAxis = 'datum-axis',
  /** 基准面。 */
  DatumPlane = 'datum-plane',
  /** 特征直线。 */
  FeatureLine = 'feature-line',
  /** 特征面。 */
  FeatureFace = 'feature-face',
}

/**
 * 沿实体移动选择阶段。
 */
export enum MoveAlongEntitySelectionPhase {
  /** 等待用户选择参考实体。 */
  AwaitingReference = 'awaiting-reference',
  /** 已选参考实体，等待用户选择零部件并拖拽。 */
  AwaitingComponent = 'awaiting-component',
}

/**
 * 装配体移动工具主模式。
 */
export enum AssemblyTransformToolMode {
  /** 移动模式。 */
  Move = 'move',
  /** 旋转模式。 */
  Rotate = 'rotate',
}

/**
 * 旋转输入模式。
 */
export enum MoveComponentRotateInputMode {
  /** 自由拖动旋转。 */
  FreeDrag = 'free-drag',
  /** 相对实体旋转。 */
  AroundEntity = 'around-entity',
  /** 通过 Delta XYZ 旋转。 */
  DeltaXYZ = 'delta-xyz',
}

/**
 * 移动零部件控制器生命周期。
 */
export enum MoveComponentLifecycle {
  /** 未激活。 */
  Closed = 'closed',
  /** 已激活。 */
  Open = 'open',
  /** 已提交。 */
  Committed = 'committed',
  /** 已取消。 */
  Cancelled = 'cancelled',
}

/**
 * 移动零部件控制器激活来源。
 */
export enum MoveComponentActivationSource {
  /** 当前未由任何入口激活。 */
  None = 'none',
  /** 装配模式空闲态静默提供默认 Move 能力。 */
  DefaultCapability = 'default-capability',
  /** 用户显式打开移动/旋转零部件命令。 */
  Command = 'command',
}

/**
 * 移动零部件状态快照。
 */
export interface MoveComponentState {
  /** 工具是否处于激活状态。 */
  active: boolean;
  /** 生命周期状态。 */
  lifecycle: MoveComponentLifecycle;
  /** 当前激活来源。 */
  activationSource: MoveComponentActivationSource;
  /** 当前主模式。 */
  toolMode: AssemblyTransformToolMode;
  /** 当前移动输入模式。 */
  moveInputMode: MoveComponentInputMode;
  /** 当前沿实体参考对象 ID。 */
  alongEntityReferenceObjectId: string | null;
  /** 当前沿实体参考显示名称。 */
  alongEntityReferenceLabel: string | null;
  /** 当前沿实体参考对象类型。 */
  alongEntityReferenceType: MoveAlongEntityReferenceType | null;
  /** 当前沿实体约束类型。 */
  alongEntityConstraintKind: MoveAlongEntityConstraintKind | null;
  /** 当前沿实体选择阶段。 */
  alongEntitySelectionPhase: MoveAlongEntitySelectionPhase;
  /** 由 Delta XYZ 的输入值。 */
  deltaXYZ: MoveComponentXYZTuple;
  /** 到 XYZ 位置的输入值。 */
  targetXYZPosition: MoveComponentXYZTuple;
  /** 当前旋转输入模式。 */
  rotateInputMode: MoveComponentRotateInputMode;
  /** 旋转 Delta XYZ 的输入值。 */
  rotateDeltaXYZ: MoveComponentXYZTuple;
  /** 当前选项模式。 */
  optionMode: MoveComponentOptionMode;
  //------ 移动旋转零部件碰撞检查选项 开始------
  /** 当前碰撞检查范围。 */
  collisionCheckScope: MoveComponentCollisionCheckScope;
  /** “这些零部件之间”模式下参与碰撞检查的零部件对象 ID。 */
  collisionCheckComponentObjectIds: readonly string[];
  /** 当前碰撞检查零部件选择阶段。 */
  collisionSelectionPhase: MoveComponentCollisionSelectionPhase;
  /** 是否在碰撞时停止。 */
  collisionStopAtCollision: boolean;
  /** 是否仅检查被拖动的主零部件。 */
  collisionDraggedPartOnly: boolean;
  //------ 移动旋转零部件碰撞检查选项 结束------
}

/**
 * 控制器视图模型。
 */
export interface MoveComponentViewModel {
  /** 当前状态。 */
  state: MoveComponentState;
  /** 当前是否允许提交。 */
  canSubmit: boolean;
}

/**
 * 控制器选项。
 */
export interface MoveComponentControllerOptions {
  /** 装配体 move / rotate / fix / float 提交后的历史记录回调。 */
  onAssemblyTransformCommitted?: (record: AssemblyTransformHistoryRecord) => AssemblyTransformHistoryCommitResult;
}
